import { useMemo, useState } from 'react'
import { Factory, Prohibit } from '@phosphor-icons/react'
import { Badge, DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../../api/core'
import { stepLabel, useMachines, useVoidMachineLabel, type MachineRow } from '../../api/process'
import { useCrops } from '../../api/reference'
import { useSites } from '../../api/store'
import { ActionButton } from '../../components/Can'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { FilterBar, SelectFilter } from '../../components/FilterBar'
import { ConsolePage } from '../../components/Layout'
import { Code, RefLink, Status, When } from '../../components/bits'
import { bandText } from '../../model/process/format'
import { useUrlState } from '../../lib/useUrlState'

const LIMIT = 50

const VOID_REASONS = [
  { value: 'Torn or unreadable', label: 'Torn or unreadable' },
  { value: 'Stuck on the wrong machine', label: 'Stuck on the wrong machine' },
  { value: 'Label missing', label: 'Label missing' },
]

/**
 * Processing machines: their label, steps, capacity, yield ranges and last run. Labels are bound on
 * the phone at the machine; the office can only void a torn or wrong one.
 */
export function MachinesPage() {
  const [f, setF, { reset, active }] = useUrlState({ siteId: '', unlabelled: '', page: 1 })
  const sites = useSites()
  const crops = useCrops()
  const cropName = useMemo(() => new Map((crops.data ?? []).map((c) => [c.id, c.name])), [crops.data])
  const q = useMachines({ page: f.page, limit: LIMIT, siteId: f.siteId || undefined, unlabelled: f.unlabelled === '' ? undefined : f.unlabelled === 'true' })
  const [voiding, setVoiding] = useState<MachineRow | null>(null)
  const voidLabel = useVoidMachineLabel()

  const columns: Column<MachineRow>[] = [
    {
      id: 'machine',
      header: 'Machine',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span className="flex items-center gap-2">
            <Code>{r.code}</Code>
            {!r.active && <Badge>Not in use</Badge>}
          </span>
          <span className="text-[12.5px] text-muted">{r.name}</span>
        </span>
      ),
    },
    { id: 'place', header: 'Place', cell: (r) => r.locationPath ?? '—', hideBelow: 'md' },
    {
      id: 'steps',
      header: 'Steps',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span>{r.stepCodes.length ? r.stepCodes.map(stepLabel).join(', ') : '—'}</span>
          <span className="text-[12px] text-muted">
            {r.capacityKgH !== null ? `${r.capacityKgH} kg/h` : 'Capacity not set'}
            {r.sharedUse ? ' · shared use' : ' · dedicated'}
          </span>
        </span>
      ),
    },
    {
      id: 'label',
      header: 'Label',
      cell: (r) =>
        r.labelPayload ? (
          <Code>{r.labelPayload}</Code>
        ) : (
          <span className="flex flex-col gap-0.5 leading-tight">
            <Status status="PENDING" label="No label" />
            <span className="max-w-[16rem] text-[12px] text-muted">Bind a label by scanning it on the phone at the machine.</span>
          </span>
        ),
    },
    {
      id: 'settings',
      header: 'Yield ranges',
      cell: (r) =>
        r.settings.length === 0 ? (
          <RefLink to={`/c/yield-bands?equipmentId=${r.id}`} className="font-sans text-[13px] text-warn">
            No range set
          </RefLink>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {r.settings.map((s) => (
              <li key={s.id}>
                <RefLink
                  to={`/c/yield-bands?cropId=${s.cropId}&stepCode=${s.stepCode}${s.equipmentId ? `&equipmentId=${s.equipmentId}` : ''}`}
                  className="font-sans text-[13px]"
                >
                  {cropName.get(s.cropId) ?? 'Crop'} · {stepLabel(s.stepCode)} · {bandText(s)} · v{s.version}
                </RefLink>
              </li>
            ))}
          </ul>
        ),
      hideBelow: 'sm',
    },
    {
      id: 'last',
      header: 'Last run',
      cell: (r) =>
        r.lastRun ? (
          <span className="flex flex-col leading-tight">
            <RefLink to={`/c/runs/${r.lastRun.id}`}>{r.lastRun.code}</RefLink>
            <span className="flex items-center gap-1 text-[12px] text-muted">
              <Status status={r.lastRun.status} /> <When at={r.lastRun.createdAt} relative />
            </span>
          </span>
        ) : (
          <span className="text-muted">No runs</span>
        ),
      hideBelow: 'lg',
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) =>
        r.labelPayload ? (
          <span onClick={(e) => e.stopPropagation()}>
            <ActionButton perm="equipment:void-label" hide size="sm" variant="ghost" leadingIcon={<Prohibit />} onClick={() => setVoiding(r)}>
              Void label
            </ActionButton>
          </span>
        ) : null,
    },
  ]

  return (
    <ConsolePage
      eyebrow="Process"
      title="Machines"
      description="Processing machines with their label, steps, yield ranges and last run. Machine details are changed on the Equipment page; a label is bound by scanning it on the phone at the machine."
    >
      <DataTable<MachineRow>
        caption="Machines"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Site"
              value={f.siteId}
              onChange={(siteId) => setF({ siteId })}
              options={(sites.data ?? []).map((s) => ({ value: s.id, label: s.name || s.code }))}
            />
            <SelectFilter
              label="Label"
              value={f.unlabelled as '' | 'true'}
              onChange={(unlabelled) => setF({ unlabelled })}
              options={[{ value: 'true', label: 'No label yet' }]}
              allLabel="Any"
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<Factory />}
            title={active ? 'No machines match' : 'No processing machines yet'}
            description="Add processing machines on the Equipment page with their steps and site."
          />
        }
      />
      <ConfirmDialog
        open={Boolean(voiding)}
        onClose={() => setVoiding(null)}
        title={`Void the label of ${voiding?.code ?? 'this machine'}?`}
        description="The label stops working and the machine has no label until a new one is scanned on the phone at the machine. Past runs keep the machine."
        confirmLabel="Void label"
        tone="danger"
        loading={voidLabel.isPending}
        reason={{ label: 'Why', choices: VOID_REASONS, allowOther: true, pattern: /^[\s\S]{1,60}$/, patternMessage: 'At most 60 characters', multiline: false }}
        onConfirm={async (reason) => {
          if (!voiding) return
          await voidLabel.mutateAsync({ id: voiding.id, reason })
          setVoiding(null)
        }}
      />
    </ConsolePage>
  )
}
